import React, { useMemo } from 'react';
import {
  ActivityIndicator,
  Image,
  Linking,
  ScrollView,
  View,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { router } from 'expo-router';

import { ThemedText } from '@/components/themed-text';
import { PressableScale } from '@/components/ui/pressable-scale';
import { EmptyState } from '@/components/ui/empty-state';
import { PublicBrandHeader } from '@/components/public/public-brand-header';
import { PublicBrandFooter } from '@/components/public/public-brand-footer';
import { MemberAvatar } from '@/components/team/member-avatar';
import {
  DEFAULT_BRAND_COLOR,
  GalleryCard,
  Grid,
  SectionHeader,
  ServiceCard,
  formatPhoneBR,
} from '@/components/public-profile/public-profile-page';
import { buildWhatsappLink, usePublicProfileView } from '@/hooks/use-public-profile';
import { COVER_ASPECT } from '@/constants/cover';
import { PublicProfileView } from '@/types/public-profile';
import { useThemedHex } from '@/hooks/use-theme-colors';
import { SeoHead } from '@/components/seo-head';
import { PROFILE_NOT_FOUND_TITLE, professionalMeta } from '@/lib/seo/profile-meta';

type Service = NonNullable<PublicProfileView['services']>[number];

/**
 * A página da pessoa dentro da loja: a marca é da organização, o conteúdo é
 * dele — foto, apresentação, os serviços que ele faz e o portfólio dele. A
 * página da loja continua sendo a de cima; daqui se volta para ela.
 */
export function PublicProfessionalPage({
  slug,
  providerId,
  onServicePress,
}: {
  slug: string;
  providerId: string;
  /** Agendar: chamado com a cópia do serviço que é dele. */
  onServicePress?: (serviceId: string) => void;
}) {
  const th = useThemedHex();
  const { data: profile, isLoading, error } = usePublicProfileView(slug);
  const { width } = useWindowDimensions();

  const member = profile?.team?.find((m) => m.providerId === providerId) ?? null;
  const brand = profile?.brandColor ?? DEFAULT_BRAND_COLOR;

  const isTablet = width >= 768;
  const isWide = width >= 1024;
  const containerMax = isWide ? 1120 : isTablet ? 760 : width;
  const avatarSize = isWide ? 128 : isTablet ? 112 : 80;
  // Quanto da foto sobe na capa; o nome começa logo abaixo da borda dela.
  const avatarOverlap = Math.round(avatarSize * 0.45);
  const serviceColumns = isWide ? 3 : isTablet ? 2 : 1;
  const galleryColumns = isWide ? 4 : isTablet ? 3 : 2;

  // O catálogo manda no card (nome, descrição e foto são do negócio); da cópia
  // dele saem o id que o agendamento usa, o preço e a duração.
  const services = useMemo<Service[]>(() => {
    if (!member) return [];
    const catalog = profile?.catalog ?? null;
    if (catalog?.length) {
      const list: Service[] = [];
      for (const item of catalog) {
        const mine = item.professionals.find((p) => p.providerId === providerId);
        if (!mine) continue;
        list.push({
          id: mine.serviceId,
          name: item.name,
          description: item.description,
          price: mine.price,
          priceDisplayMode: mine.priceDisplayMode,
          durationMinutes: mine.durationMinutes ?? item.durationMinutes,
          billingInterval: mine.billingInterval ?? null,
          catalogItemId: item.id,
        });
      }
      return list;
    }
    return member.services;
  }, [member, profile?.catalog, providerId]);

  const gallery = useMemo(
    () => (profile?.gallery ?? []).filter((photo) => photo.providerId === providerId),
    [profile?.gallery, providerId],
  );

  // O número é o dele; sem cadastro, o da loja responde por ele.
  const phone = member?.whatsappNumber ?? profile?.whatsappNumber ?? null;
  const openWhatsapp = () => {
    if (!phone) return;
    void Linking.openURL(buildWhatsappLink(phone, 'profile'));
  };

  const goToShop = () => router.push({ pathname: '/[handle]', params: { handle: `@${slug}` } });

  if (isLoading) {
    return (
      <View className="flex-1 items-center justify-center bg-neutral-50">
        <ActivityIndicator size="large" color={DEFAULT_BRAND_COLOR} />
      </View>
    );
  }

  // Sem equipe (ou id que não é de ninguém dela) a página da pessoa não existe:
  // a da loja responde por tudo.
  if (error || !profile || !member) {
    return (
      <View className="flex-1 items-center justify-center gap-4 bg-neutral-50 p-8">
        {/* Mesmo título e noindex do 404 que a função da Vercel responde. */}
        <SeoHead title={PROFILE_NOT_FOUND_TITLE} noindex />
        <EmptyState
          icon="person-circle-outline"
          title="Perfil não encontrado"
          description="Esta pessoa não está na equipe desta página."
        />
        <PressableScale
          haptic
          onPress={goToShop}
          className="rounded-full px-5 py-2.5"
          style={{ backgroundColor: DEFAULT_BRAND_COLOR, borderCurve: 'continuous' }}
        >
          <ThemedText className="font-bold text-sm text-white">Ver a página</ThemedText>
        </PressableScale>
      </View>
    );
  }

  const meta = professionalMeta(profile, member);

  return (
    <View className="flex-1 bg-neutral-50">
      <SeoHead title={meta.title} description={meta.description} path={meta.path} />
      <ScrollView
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={{ flexGrow: 1, paddingBottom: 48 }}
      >
        <PublicBrandHeader />
        <View className="mx-auto w-full" style={{ maxWidth: containerMax }}>
          {/* Capa: é da loja — a marca continua sendo dela. */}
          <View
            className="overflow-hidden"
            style={{
              borderBottomLeftRadius: isTablet ? 28 : 0,
              borderBottomRightRadius: isTablet ? 28 : 0,
              borderCurve: 'continuous',
            }}
          >
            {profile.coverUrl ? (
              <Image
                source={{ uri: profile.coverUrl }}
                accessibilityLabel={`Capa de ${profile.businessName}`}
                style={{ width: '100%', aspectRatio: COVER_ASPECT }}
                resizeMode="cover"
              />
            ) : (
              <LinearGradient
                colors={[brand, `${brand}B3`]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={{ width: '100%', aspectRatio: COVER_ASPECT }}
              />
            )}
          </View>

          {/* Identidade: foto à esquerda sobe na capa, nome ao lado — nada a centralizar */}
          <View className={`flex-row items-start gap-3 ${isTablet ? 'gap-5 px-6' : 'px-4'}`}>
            <View style={{ marginTop: -avatarOverlap }}>
              <View
                className="overflow-hidden rounded-full bg-card"
                style={{
                  borderWidth: 4,
                  borderColor: th('#fff'),
                  borderRadius: (avatarSize + 8) / 2,
                  boxShadow: '0 6px 20px rgba(23,17,10,0.18)',
                }}
              >
                <MemberAvatar
                  name={member.name}
                  image={member.image}
                  color={brand}
                  size={avatarSize}
                />
              </View>
            </View>
            <View className={`min-w-0 flex-1 items-start ${isTablet ? 'pt-4' : 'pt-2.5'}`}>
              <ThemedText
                role="heading"
                aria-level={1}
                numberOfLines={2}
                className={isTablet ? 'font-bold text-3xl' : 'font-bold text-xl leading-7'}
              >
                {member.name}
              </ThemedText>

              {/* De onde ele é: toca e volta para a página da loja. */}
              <PressableScale
                haptic
                onPress={goToShop}
                accessibilityRole="button"
                accessibilityLabel={`Ver ${profile.businessName}`}
                className="mt-1.5 max-w-full flex-row items-center gap-1.5 rounded-full px-3 py-1"
                style={{ backgroundColor: `${brand}1A` }}
              >
                <Ionicons name="storefront-outline" size={13} color={brand} />
                <ThemedText
                  numberOfLines={1}
                  className="shrink font-semibold text-xs"
                  style={{ color: brand }}
                >
                  {profile.businessName}
                </ThemedText>
                <Ionicons name="chevron-forward" size={12} color={brand} />
              </PressableScale>
            </View>
          </View>

          <View className={isTablet ? 'mt-4 flex-row items-start gap-6 px-6' : 'mt-3 gap-3 px-4'}>
            {!!member.bio && (
              <ThemedText
                className={`text-[15px] leading-6 text-neutral-600 ${isTablet ? 'min-w-0 flex-1' : ''}`}
              >
                {member.bio}
              </ThemedText>
            )}

            {/* O número dele, à vista: toca e abre a conversa. */}
            {!!phone && (
              <PressableScale
                haptic
                onPress={openWhatsapp}
                accessibilityRole="button"
                accessibilityLabel={`Chamar ${member.name} no WhatsApp: ${formatPhoneBR(phone)}`}
                className={`h-12 flex-row items-center justify-center gap-2 rounded-full px-5 ${isTablet ? 'shrink-0' : ''}`}
                style={{ backgroundColor: brand, borderCurve: 'continuous' }}
              >
                <Ionicons name="logo-whatsapp" size={18} color="#fff" />
                <ThemedText numberOfLines={1} className="font-bold text-sm text-white">
                  {formatPhoneBR(phone)}
                </ThemedText>
              </PressableScale>
            )}
          </View>

          <View className={`gap-8 ${isTablet ? 'px-6' : 'px-4'} pt-8`}>
            {services.length > 0 && (
              <View className="gap-4">
                <SectionHeader
                  icon="pricetags-outline"
                  title="Serviços"
                  count={services.length}
                  brandColor={brand}
                />
                <Grid columns={serviceColumns}>
                  {services.map((service) => (
                    <ServiceCard
                      key={service.id}
                      service={service}
                      brandColor={brand}
                      onPress={onServicePress ? () => onServicePress(service.id) : undefined}
                    />
                  ))}
                </Grid>
              </View>
            )}

            {gallery.length > 0 && (
              <View className="gap-4">
                <SectionHeader
                  icon="images-outline"
                  title="Trabalhos"
                  count={gallery.length}
                  brandColor={brand}
                />
                <Grid columns={galleryColumns}>
                  {gallery.map((photo) => (
                    <GalleryCard key={photo.id} photo={photo} />
                  ))}
                </Grid>
              </View>
            )}

            {services.length === 0 && gallery.length === 0 && (
              <EmptyState
                icon="sparkles-outline"
                title="Nada por aqui ainda"
                description={`${member.name} ainda não publicou serviços nem trabalhos.`}
              />
            )}
          </View>

          <PublicBrandFooter reportSlug={slug} />
        </View>
      </ScrollView>
    </View>
  );
}
